import { Stack } from '@mui/material';
import { InputSelect } from '../elements/InputSelect';
import { InputText } from '../elements/InputText';
import { SelectChangeEvent } from '@mui/material/Select';
import { Controller, useFormContext } from 'react-hook-form';

import { DropdownValueOption } from 'hooks/useGetOptionStartInspection';
import { capitalizeFirstLetter } from 'utils/convertData';
import React from 'react';
import dayjs from 'dayjs';
import { DatePicker } from '../elements/DatePicker';
import { SingleEntryField } from 'services/option';

type Props = {
  roomDetailName: string;
  complianceOption: { label: string; value: string }[];
  roomOptions: DropdownValueOption[];
  roomSingleField: SingleEntryField[];
};

export const FormDetailEnd = ({
  roomSingleField,
  roomDetailName,
  complianceOption,
  roomOptions
}: Props) => {
  const { control, watch, setValue } = useFormContext();
  return (
    <Stack gap={2} width="100%">
      {/* Compliance Representative */}
      <Controller
        name="locationDetail.complianceRepresentative"
        control={control}
        render={({ field, fieldState }) => (
          <InputSelect
            title="Compliance Representative"
            value={field.value}
            placeholder="Select Compliance Representative"
            inputError={fieldState.error?.message}
            onChange={(e: SelectChangeEvent<unknown>) => field.onChange(e.target.value)}
            options={complianceOption.map((item) => ({
              label: item?.label,
              value: item?.value?.toString()
            }))}
          />
        )}
      />

      {/* Client */}
      <Controller
        name="locationDetail.client"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Client (Requestor)"
            placeholder="Client (Requestor)"
            inputError={fieldState.error?.message}
            inputProps={{ ...field, disabled: true }}
          />
        )}
      />
      {/* Client */}
      <Controller
        name="locationDetail.location"
        control={control}
        render={() => (
          <InputText
            title="Location"
            placeholder="Location"
            inputProps={{ value: roomDetailName, disabled: true }}
          />
        )}
      />
      <Controller
        name="locationDetail.roomId1"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="RoomId 1"
            placeholder="RoomId 1"
            inputError={fieldState.error?.message}
            inputProps={{ ...field }}
          />
        )}
      />
      <Controller
        name="locationDetail.roomId2"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="RoomId 2"
            placeholder="RoomId 2"
            inputError={fieldState.error?.message}
            inputProps={{ ...field }}
          />
        )}
      />
      <Controller
        control={control}
        render={({ field: { value }, fieldState }) => (
          <DatePicker
            inputError={fieldState?.error?.message || ''}
            style={{ flexDirection: 'column' }}
            title={'Choose Date'}
            onChange={(val) => setValue(`locationDetail.date`, val ? dayjs(val).toISOString() : '')}
            value={value ? dayjs(value) : undefined}
          />
        )}
        name={'locationDetail.date'}
      />
      {/* Approver */}
      <Controller
        name="locationDetail.approver"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Approver"
            placeholder="Approver"
            inputError={fieldState.error?.message}
            inputProps={{ ...field }}
          />
        )}
      />

      {/* Document Control */}
      <Controller
        name="locationDetail.documentControl"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Document Control"
            placeholder="Document Control"
            inputError={fieldState.error?.message}
            inputProps={{ ...field, disabled: true }}
          />
        )}
      />

      {roomOptions?.map((opt) => {
        const fieldName = `locationDetail.option_${opt.id}`;
        const otherFieldName = `locationDetail.option_${opt.id}_name`;
        return (
          <React.Fragment key={opt.id}>
            <Controller
              name={fieldName}
              control={control}
              render={({ field, fieldState }) => (
                <InputSelect
                  title={capitalizeFirstLetter(opt.name.replaceAll('_', ' '))}
                  value={field.value}
                  placeholder={`Select ${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))}`}
                  inputError={fieldState.error?.message}
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    const val = e.target.value;
                    field.onChange(val);

                    // Reset Action By if choosing OTHER
                    if (val === 'OTHER') {
                      setValue('locationDetail.actionBy', '');
                    }
                  }}
                  options={opt.dropdownValues.map((dv) => ({
                    label: dv.value,
                    value: dv.value
                  }))}
                />
              )}
            />

            {/* OTHER */}
            {watch(fieldName) === 'OTHER' && (
              <Controller
                name={otherFieldName}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <InputText
                    title={`${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))} Name`}
                    placeholder={`${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))} Name`}
                    inputError={fieldState.error ? 'This field is required' : ''}
                    inputProps={{
                      ...field,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onChange: (e: any) => {
                        field.onChange(e.target.value);
                        setValue('locationDetail.actionBy', '');
                      }
                    }}
                  />
                )}
              />
            )}
          </React.Fragment>
        );
      })}
      {roomSingleField?.map((field) => (
        <React.Fragment key={field.id}>
          {field.type === 'DATE' ? (
            <Controller
              name={`locationDetail.single_${field.id}`}
              control={control}
              render={({ field: f }) => (
                <DatePicker
                  title={`${field.name}:`}
                  onChange={(val) =>
                    setValue(
                      `locationDetail.single_${field.id}`,
                      val ? dayjs(val).toISOString() : ''
                    )
                  }
                  value={f.value ? dayjs(f.value) : undefined}
                />
              )}
            />
          ) : (
            <Controller
              name={`locationDetail.single_${field.id}`}
              control={control}
              render={({ field: f, fieldState }) => (
                <InputText
                  title={`${field.name}:`}
                  placeholder={`Enter ${field.name}`}
                  inputProps={{ ...f }}
                  inputError={fieldState.error?.message}
                />
              )}
            />
          )}
        </React.Fragment>
      ))}
    </Stack>
  );
};
